import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import {
  EDistributionChannelType,
  IDistributionChannel,
} from '../../../../../types/distribution-channel'
import { ImageLinks, LogoTypeSocial } from '../../../../../constants/image-link'
import CircularLoading from '../../../../../components/CircularProgress'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { ITikTokAccount } from '../../../../../types/tiktok'
import { useUser } from '../../../../../hooks/useUser'
import { isAllowTikTok } from '../../../../../constants/user'

interface IDistributionChannelFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  distributionChannel?: IDistributionChannel
  onUpdateDistributionChannelsSuccess: () => void
}

interface IDistributionChannelFormModal {
  name: string
  type: EDistributionChannelType | string
  url: string
  ref?: string
  isActive?: boolean
}

const menuItemsType = [
  {
    label: 'Facebook',
    value: EDistributionChannelType.FACEBOOK,
  },
  {
    label: 'Tiktok',
    value: EDistributionChannelType.TIKTOK,
  },
  {
    label: 'Instagram',
    value: EDistributionChannelType.INSTAGRAM,
  },
  {
    label: 'Shopee',
    value: EDistributionChannelType.SHOPEE,
  },
  {
    label: 'Lazada',
    value: EDistributionChannelType.LAZADA,
  },
  {
    label: 'Line',
    value: EDistributionChannelType.LINE,
  },
  {
    label: 'อื่นๆ',
    value: EDistributionChannelType.ETC,
  },
]

const getDistributionChannelFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string()
      .required('กรุณากรอกชื่อหมวดหมู่')
      .min(2, 'ชื่อหมวดหมู่ต้องมีอย่างน้อย 2 ตัวอักษร'),
    type: Yup.string().required('กรุณาเลือกช่องทางการขาย'),
    url: Yup.string().required('กรุณากรอก URL'),
    isActive: Yup.boolean().required('กรุณาเลือกสถานะ'),
  })
}

export const DistributionChannelFormModal = (
  props: IDistributionChannelFormModalProps,
) => {
  const {
    isOpen,
    onClose,
    distributionChannel,
    onUpdateDistributionChannelsSuccess,
  } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { merchant } = useUser()

  const { data: tiktokAccounts, isLoading: isLoadingTiktokAccounts } = useAxios<
    ITikTokAccount[]
  >({
    method: 'GET',
    url: '/tiktok/merchant/accounts',
  })

  useEffect(() => {
    setIsLoading(true)
    formik.setValues({
      name: distributionChannel?.name || '',
      type: (distributionChannel?.type as EDistributionChannelType) || '',
      url: distributionChannel?.url || '',
      ref: distributionChannel?.ref || '',
      isActive: distributionChannel?.isActive || false,
    })
    setIsLoading(false)
  }, [isOpen, distributionChannel])

  const formik = useFormik<IDistributionChannelFormModal>({
    initialValues: {
      name: '',
      type: '',
      url: '',
      ref: '',
      isActive: false,
    } as IDistributionChannelFormModal,
    validationSchema: getDistributionChannelFormValidationSchema(),
    onSubmit: async (values: IDistributionChannelFormModal) => {
      setIsLoading(true)
      const isUpdate = !!distributionChannel
      try {
        const response = await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate
            ? `/distribution-channels/${distributionChannel.objectId}`
            : '/distribution-channels',
          data: {
            ...values,
          },
        })
        const { status } = response
        message.success(
          status === 201
            ? `สร้างช่องทางการขาย ${values.name} สำเร็จ`
            : `แก้ไขช่องทางการขาย ${values?.name} สำเร็จ`,
        )
        formik.resetForm()
        onClose(false)
        onUpdateDistributionChannelsSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '640px!important',
        },
      }}
    >
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>
        {distributionChannel ? 'แก้ไขช่องทางการขาย' : 'เพิ่มช่องทางการขาย'}
      </DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="type-label">ช่องทางการขาย</InputLabel>
                <Select
                  id="type"
                  name="type"
                  labelId="type-label"
                  value={formik.values.type}
                  onChange={(e) => {
                    formik.handleChange(e)
                    formik.setFieldValue('ref', '')
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e)
                    formik.setFieldValue('ref', '')
                  }}
                  fullWidth
                >
                  {menuItemsType.map((item, index: number) => (
                    <MenuItem value={item.value} key={index}>
                      <img
                        style={{ marginRight: 5 }}
                        src={
                          ImageLinks[
                            `LOGO_${item.value.toUpperCase()}` as LogoTypeSocial
                          ]
                        }
                        alt="smallLogoSocial"
                        height={25}
                      />
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formik.values.type === EDistributionChannelType.TIKTOK &&
            isAllowTikTok(merchant) ? (
              <Grid item xs={12} lg={12}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="ref">เลือกร้านค้า TikTok</InputLabel>
                  <Select
                    id="ref"
                    labelId="ref"
                    name="ref"
                    value={formik.values.ref}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                  >
                    {tiktokAccounts?.map((account) => (
                      <MenuItem value={account.shopId} key={account.shopId}>
                        {account.sellerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} lg={12}>
                <TextField
                  id="ref"
                  fullWidth
                  label="รหัสอ้างอิง (เช่น id ของ Facebook, line@ หรือ social media อื่นๆ)"
                  name="ref"
                  helperText={
                    formik.touched.ref && (formik.errors.ref as string)
                  }
                  error={!!(formik.touched.ref && formik.errors.ref)}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.ref}
                />
              </Grid>
            )}
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อช่องทางการขาย"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="url"
                fullWidth
                label="URL"
                name="url"
                helperText={formik.touched.url && (formik.errors.url as string)}
                error={!!(formik.touched.url && formik.errors.url)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.url}
              />
            </Grid>

            <Grid item xs={12} lg={12}>
              <label>เปิดการใช้งาน</label>
              <Switch
                checked={formik.values.isActive}
                onChange={(e) =>
                  formik.setFieldValue('isActive', e.target.checked)
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {distributionChannel
                  ? 'แก้ไขช่องทางการขาย'
                  : 'เพิ่มช่องทางการขาย'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
